import React, { useState, useEffect } from 'react';
import { Heart, Send, UserPlus, MessageCircle, Loader2, ExternalLink } from 'lucide-react';
import { apiClient } from '../../api/client';
import { useNavigate } from 'react-router-dom';

const SocialConnectionsCard = () => {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [pending, setPending] = useState({ sent: [], received: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [connectionsRes, pendingRes] = await Promise.allSettled([
        apiClient.getConnections(),
        apiClient.getPendingInvitations()
      ]);

      if (connectionsRes.status === 'fulfilled') {
        setConnections(connectionsRes.value || []);
      }
      if (pendingRes.status === 'fulfilled') {
        setPending(pendingRes.value || { sent: [], received: [] });
      }
    } catch (err) {
      console.error('Failed to fetch connections:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ava_chat':
        return <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">Ava Chat</span>;
      case 'human_chat':
        return <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">Direct Chat</span>;
      case 'graduated':
        return <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Graduated</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="card" data-testid="social-connections-card">
        <div className="card-header">
          <h2 className="card-title">Connections</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  const totalPending = pending.sent.length + pending.received.length;
  const hasContent = connections.length > 0 || totalPending > 0;

  return (
    <div className="card" data-testid="social-connections-card">
      <div className="card-header">
        <h2 className="card-title">Connections</h2>
        <button
          onClick={() => navigate('/connections')}
          className="card-action"
        >
          View All
        </button>
      </div>

      {!hasContent ? (
        <div className="text-center py-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-pink-100 rounded-full flex items-center justify-center">
            <Heart size={28} className="text-pink-500" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">No connections yet</h3>
          <p className="text-sm text-gray-600 mb-4">
            Invite a real person to connect and cheer each other on
          </p>
          <button
            onClick={() => navigate('/connections/invite')}
            className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2 mx-auto"
          >
            <Send size={16} />
            Create Invitation
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Pending Invitations */}
          {totalPending > 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserPlus size={18} className="text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">
                    {totalPending} pending invitation{totalPending > 1 ? 's' : ''}
                  </span>
                </div>
                <button
                  onClick={() => navigate('/connections/pending')}
                  className="text-xs text-yellow-700 hover:underline"
                >
                  View
                </button>
              </div>
            </div>
          )}

          {/* Active Connections */}
          {connections.slice(0, 3).map((conn) => (
            <div
              key={conn.connection_id}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-pink-300 hover:bg-pink-50 transition-all cursor-pointer"
              onClick={() => navigate(`/connections/${conn.connection_id}`)}
            >
              <div className="flex items-center gap-3">
                {conn.other_ava?.avatar_url ? (
                  <img
                    src={conn.other_ava.avatar_url}
                    alt={conn.other_user_name}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white font-bold">
                    {conn.other_user_name?.charAt(0) || '?'}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{conn.other_user_name}</p>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(conn.status)}
                    <span className="text-xs text-gray-600">{formatTime(conn.last_message_at)}</span>
                  </div>
                </div>
              </div>
              <button className="p-2 hover:bg-pink-100 rounded-lg transition-colors">
                <MessageCircle size={18} className="text-pink-500" />
              </button>
            </div>
          ))}

          {/* Create New Invitation */}
          <button
            onClick={() => navigate('/connections/invite')}
            className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-pink-400 hover:text-pink-600 transition-colors flex items-center justify-center gap-2"
          >
            <Send size={18} />
            <span className="font-medium">Send New Invitation</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SocialConnectionsCard;
